import React from 'react';
import PropTypes from 'prop-types';
import {
    LOCATION_END_OF_TEXT,
    LOCATION_RIGHT,
    REQUIRED_INDICATOR_LOCATION,
    TYPE_SPAN,
    TYPE_LABEL,
    TYPES,
} from './constants';
import classNames from 'classnames';
import { gettext } from '@eventbrite/legacy-infra.i18n';

import styles from './Label.module.scss';

const LabelSpan = ({ children, dataTestId, ...componentAttrs }) => {
    let labelDataTestId = 'eds-label-span';

    if (dataTestId) {
        labelDataTestId = dataTestId;
    }

    return (
        <span {...componentAttrs} data-testid={labelDataTestId}>
            {children}
        </span>
    );
};

const LabelLabel = ({ children, htmlFor, dataTestId, ...componentAttrs }) => {
    let labelDataTestId = 'eds-label-label';

    if (dataTestId) {
        labelDataTestId = dataTestId;
    }

    return (
        <label {...componentAttrs} htmlFor={htmlFor} data-testid={labelDataTestId}>
            {children}
        </label>
    );
};

const TYPES_COMPONENT_MAP = {
    [TYPE_SPAN]: LabelSpan,
    [TYPE_LABEL]: LabelLabel,
};

/**
 * Class that represents a label for a single or multiple inputs.
 * @extends React.PureComponent
 * @example
 * // <Label htmlFor="my-input" type="span">My Label</Label>
 **/
export class Label extends React.PureComponent {
    static propTypes = {
        children: PropTypes.node.isRequired,
        /**
         * An id string referencing the input this label pertains to.
         */
        htmlFor: PropTypes.string,
        /**
         * One of 'label' or 'span'.
         */
        type: PropTypes.oneOf(TYPES),
        /**
         * Whether or not the field is required.
         */
        required: PropTypes.bool,
        /**
         * Whether or not the field is hidden.
         */
        hidden: PropTypes.bool,
        /**
         * Identifier for tests
         */
        'data-testid': PropTypes.string,
        /**
         * Static state of the input field
         */
        shouldShowAsStatic: PropTypes.bool,
        /**
         * True if the app rebrand is active
         */
        isAppRebrandActive: PropTypes.bool,
        /**
         * The location of the required indicator.
         */
        requiredIndicatorLocation: PropTypes.oneOf(REQUIRED_INDICATOR_LOCATION),
        /**
         * Custom CSS classname to apply to divider
         */
        customClassNames: PropTypes.object,
    };

    static defaultProps = {
        type: TYPE_LABEL,
        required: false,
        hidden: false,
        customClassNames: {},
    };

    _getRequiredIndicator(shouldShowAsStatic) {
        const { customClassNames } = this.props;
        const requiredIndicatorClassName = classNames(styles.requiredIndicator, {
            [customClassNames.requiredIndicator]: customClassNames.requiredIndicator,
            [styles.textBs]: shouldShowAsStatic,
            [styles.textBm]: !shouldShowAsStatic,
        });

        return (
            <span className={requiredIndicatorClassName} data-testid="required-indicator">
                <span> *</span>
                <span className={styles.hiddenAccessible}>({gettext('required').toString()})</span>
            </span>
        );
    }

    render() {
        const {
            children,
            type,
            required,
            hidden,
            htmlFor,
            shouldShowAsStatic,
            isAppRebrandActive,
            requiredIndicatorLocation = LOCATION_RIGHT,
            'data-testid': dataTestId,
            customClassNames,
            ...extraAttrs
        } = this.props;
        const labelClass = classNames(styles.root, {
            [styles.primary]: !hidden,
            [customClassNames.primary]: !hidden && customClassNames.primary,
            [styles.hiddenAccessible]: hidden,
            //Updated scss with https://github.com/eventbrite/eb-ui/blob/78893304280c62e2de1af258345118fe37105617/packages/eds/eds-input-field/src/inputField.scss#L101
            [styles.required]: required,
            [styles.rebrandingActive]: isAppRebrandActive,
        });
        const LabelSubComponent = TYPES_COMPONENT_MAP[type];
        let requiredIndicator;

        if (required) {
            requiredIndicator = this._getRequiredIndicator(shouldShowAsStatic);
        }
        const labelContentClass = classNames(styles.content, {
            [customClassNames.content]: customClassNames.content,
            [styles.contentRequiredEndOfText]:
                required && requiredIndicatorLocation === LOCATION_END_OF_TEXT,
        });

        const content =
            requiredIndicatorLocation === LOCATION_END_OF_TEXT ? (
                <span className={labelContentClass}>
                    {children}
                    {requiredIndicator}
                </span>
            ) : (
                <>
                    <span
                        className={classNames(styles.content, {
                            [customClassNames.content]: customClassNames.content,
                        })}
                    >
                        {children}
                    </span>
                    {requiredIndicator}
                </>
            );
        return (
            <LabelSubComponent
                className={labelClass}
                htmlFor={htmlFor}
                dataTestId={dataTestId}
                {...extraAttrs}
            >
                {content}
            </LabelSubComponent>
        );
    }
}
